// Independent bus/rail feeds share London's shell without replacing TfL.
import * as Feed from '@tol/core/feed';
import type { Network, PageLine, Station, SegmentGeometry, NetworkLine, TrainStore } from '@tol/core/types';
import { createGtfsClient } from '@tol/gtfs/client';
import type { GtfsClient } from '@tol/gtfs/client';
import { isTrains } from '@tol/gtfs/trains';
import type { Shell } from '@tol/ui/shell';
import { isBusLines } from '../shared/buses.ts';
import { NR_LINES } from '../shared/nr-lines.ts';
import { hideOperationalStations } from './rail-network.ts';

export const RAIL_LINES: readonly PageLine[] = Object.entries(NR_LINES)
  .filter(
    ([id]: readonly [string, Readonly<{ name: string; show: string }>]) => id !== 'elizabeth' && id !== 'nr-overground',
  )
  .map(([id, l]: readonly [string, Readonly<{ name: string; show: string }>]) => ({
    id,
    name: l.name,
    show: l.show,
    color: l.show,
    mode: 'tube',
    dual: false,
    label: l.name,
  }));

function publishedRailNetwork(network: Network): Network {
  return hideOperationalStations({
    ...network,
    lines: Object.fromEntries(
      Object.entries(network.lines).filter(
        ([id]: readonly [string, NetworkLine | undefined]) => id !== 'elizabeth' && id !== 'nr-overground',
      ),
    ),
  });
}

export function mergeNetworks(parts: readonly Network[]): Network {
  const stations = new Map<string, Station>();
  const lines: Partial<Record<string, import('@tol/core/types').NetworkLine>> = {};
  for (const p of parts) Object.assign(lines, p.lines);
  const geom = new Map<string, SegmentGeometry>();
  for (const p of parts) {
    for (const [id, s] of p.stations) {
      const old = stations.get(id);
      stations.set(id, old ? { ...s, lines: new Set([...old.lines, ...s.lines]) } : s);
    }
    for (const [key, shape] of p.geom ?? []) geom.set(key, shape);
  }
  return { stations, lines, geom };
}

// Exact UID takeover also retires already-published prediction markers.
// Missing-trip grace must not keep a second representation of the same run.
export function retireMappedRail(store: TrainStore, hasUid: (uid: string) => boolean, prefix = ''): boolean {
  let changed = false;
  for (const [id, train] of store.trains) {
    if (!train.pred.key.startsWith(prefix + 'nr:')) continue;
    const uid = train.pred.key.slice(prefix.length).split(':')[1] ?? '';
    if (uid === '' || !hasUid(uid)) continue;
    store.trains.delete(id);
    changed = true;
    for (const stop of train.pred.stops) {
      const arrivals = store.byStation.get(stop.sid);
      if (!arrivals) continue;
      const at = arrivals.findIndex(
        (a) => a.lineId === train.pred.lineId && a.vid === train.pred.vid && a.eta === stop.eta,
      );
      if (at < 0) continue;
      const kept = [...arrivals.slice(0, at), ...arrivals.slice(at + 1)];
      if (kept.length > 0) store.byStation.set(stop.sid, kept);
      else store.byStation.delete(stop.sid);
    }
  }
  return changed;
}

export function supplemental(
  shell: Pick<Shell, 'live' | 'now' | 'addLines' | 'isGeometryFile' | 'updateArrivalUI' | 'renderRoster'>,
  hasUid: (uid: string) => boolean,
  publish: (key: string, net: Network) => void,
): {
  readonly clients: ReadonlyMap<string, GtfsClient>;
  readonly start: () => void;
  readonly stop: () => void;
  readonly reconcileRail: () => boolean;
} {
  const reconcileRail = (): boolean => retireMappedRail(shell.live, hasUid);
  const clients = new Map<string, GtfsClient>();
  const api = Feed.createClient({ base: '' });
  let stopped = false;
  let retry: ReturnType<typeof setTimeout> | undefined;
  async function load(): Promise<void> {
    let failed = false;
    await Promise.all(
      (['rail', 'bus'] as const).map(async (name) => {
        const key = name === 'rail' ? 3 : 4;
        if (stopped) return;
        try {
          let c = clients.get(name);
          if (!c) {
            const catalog = name === 'bus' ? await api.getJSON('bus/lines.json') : null;
            if (name === 'bus' && !isBusLines(catalog)) throw new Error('Bus catalog unavailable');
            const lines = isBusLines(catalog) ? catalog : RAIL_LINES;
            shell.addLines(lines, [
              { id: name, name: name === 'bus' ? 'TfL buses' : 'National Rail', lines: lines.map((l) => l.id) },
            ]);
            const sampled: typeof api.getSampledJSON = async (url, signal) => {
              const got = await api.getSampledJSON(url, signal);
              if (name !== 'rail' || !isTrains(got.data)) return got;
              return {
                ...got,
                data: {
                  ...got.data,
                  trips: got.data.trips.filter(
                    (t) =>
                      t.line !== 'elizabeth' &&
                      t.line !== 'nr-overground' &&
                      (!t.id.startsWith('nr:') || !hasUid(t.id.split(':')[1] ?? '')),
                  ),
                },
              };
            };
            c = createGtfsClient({
              name,
              base: `${name}/`,
              version: '',
              api: { getJSON: api.getJSON, getSampledJSON: sampled },
              store: shell.live,
              key,
              lines: lines.map((l) => l.id),
              now: shell.now,
              pollMs: name === 'rail' ? 10_000 : 30_000,
              alerts: true,
              major: false,
              isGeometry: shell.isGeometryFile,
              onUpdate: () => {
                reconcileRail();
                shell.updateArrivalUI();
              },
              onProblem: () => {
                shell.renderRoster();
              },
              onNetwork: (net) => {
                publish(name, name === 'rail' ? publishedRailNetwork(net) : net);
              },
            });
            clients.set(name, c);
          }
          const net = await c.load();
          // TfL's own network remains authoritative for Elizabeth/Overground.
          publish(name, name === 'rail' ? publishedRailNetwork(net) : net);
          if (!document.hidden) void c.start();
        } catch {
          failed = true;
        }
      }),
    );
    if (failed && !stopped) retry = setTimeout(() => void load(), 30_000);
  }
  return {
    clients,
    reconcileRail,
    start: () => {
      stopped = false;
      void load();
    },
    stop: () => {
      stopped = true;
      clearTimeout(retry);
      for (const c of clients.values()) c.stop();
    },
  };
}
